import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { SignInForm } from "../auth/SignInForm";
import { SignUpForm } from "../auth/SignUpForm";
import { useAuth } from "@core/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBrandName } from "@core/services/supportProduct";
import { takeAuthReturnTo } from "@core/lib/authReturnTo";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode?: "signin" | "signup";
}

export function AuthModal({
  open,
  onOpenChange,
  defaultMode = "signin",
}: AuthModalProps) {
  const { t } = useTranslation(["auth", "common"]);
  const [mode, setMode] = useState<"signin" | "signup">(defaultMode);
  const [hasFormData, setHasFormData] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // 🔧 FIX BUG : Synchroniser le mode interne avec defaultMode quand il change
  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  // Fermer automatiquement le modal après connexion réussie
  const previousUserRef = useRef(user);

  useEffect(() => {
    const previousUser = previousUserRef.current;
    previousUserRef.current = user;

    if (!previousUser && user && !loading && open) {
      // Petit délai pour laisser le temps à l'utilisateur de voir le succès
      const timer = setTimeout(() => {
        onOpenChange(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, loading, open, onOpenChange]);

  const handleAuthSuccess = () => {
    // Fermer le modal après authentification réussie
    onOpenChange(false);

    // Rediriger vers l'URL sauvegardée si elle existe (déjà sans basename)
    const returnTo = takeAuthReturnTo();
    if (returnTo) {
      console.log("🔄 [AUTH_MODAL] Redirecting to saved path:", returnTo);
      setTimeout(() => {
        navigate(returnTo, { replace: true });
      }, 300);
    }
  };

  // Si l'utilisateur est déjà connecté, afficher un message
  if (user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("auth:already_signed_in")}</DialogTitle>
            <DialogDescription>
              {t("auth:already_signed_in_as", { email: user.email })}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-muted-foreground">
              {t("auth:sign_out_hint")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // 🔧 FIX BUG #3: Empêcher la fermeture si des données sont saisies
  const handleInteractOutside = (e: Event) => {
    if (hasFormData) {
      e.preventDefault();
      // Optionnel: afficher un message à l'utilisateur
      const shouldClose = window.confirm(t("auth:unsaved_changes_confirm"));
      if (shouldClose) {
        setHasFormData(false);
        onOpenChange(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-card border border-border text-foreground"
        onInteractOutside={handleInteractOutside}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>
            {mode === "signin" ? t("auth:sign_in") : t("auth:sign_up")}
          </DialogTitle>
          <DialogDescription>
            {mode === "signin"
              ? t("auth:sign_in_subtitle", { brand: getBrandName() })
              : t("auth:create_account_subtitle", { brand: getBrandName() })}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {mode === "signin" ? (
            <SignInForm
              onSuccess={handleAuthSuccess}
              onSwitchToSignUp={() => setMode("signup")}
              onFormChange={(hasData) => setHasFormData(hasData)}
            />
          ) : (
            <SignUpForm
              onSuccess={handleAuthSuccess}
              onSwitchToSignIn={() => setMode("signin")}
              onFormChange={(hasData) => setHasFormData(hasData)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
